import { useState, useEffect } from 'react';
import { EssenciaLogo } from '../ui/EssenciaLogo';
import type { ConsentState } from '../../hooks/useCookieConsent';

interface CookieBannerProps {
  show: boolean;
  showPreferences: boolean;
  consent: ConsentState;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSavePreferences: (state: ConsentState) => void;
  onOpenPreferences: () => void;
  onSetShowPreferences: (v: boolean) => void;
}

const categories: {
  key: keyof ConsentState;
  label: string;
  description: string;
  required: boolean;
}[] = [
  {
    key: 'analytics',
    label: 'Analíticos',
    description: 'Ajudam a entender como os visitantes interagem com o site, permitindo melhorias contínuas.',
    required: false,
  },
  {
    key: 'performance',
    label: 'Performance',
    description: 'Permitem otimizar o carregamento e a experiência do usuário.',
    required: false,
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'Usados para exibir anúncios relevantes e medir campanhas.',
    required: false,
  },
];

export function CookieBanner({
  show,
  showPreferences,
  consent,
  onAcceptAll,
  onRejectAll,
  onSavePreferences,
  onOpenPreferences,
  onSetShowPreferences,
}: CookieBannerProps) {
  const [localConsent, setLocalConsent] = useState<ConsentState>(consent);

  // Sync localConsent when parent consent changes (e.g. when reopening preferences)
  useEffect(() => {
    setLocalConsent(consent);
  }, [consent]);

  if (!show) return null;

  const toggleCategory = (key: keyof ConsentState) => {
    setLocalConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (showPreferences) {
    return (
      <div className="fixed inset-x-0 bottom-0 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm z-[9999] p-4 sm:p-0">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
          {/* Header */}
          <div className="bg-[#2D4A3E] px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-settings-3-line text-[#C9A96E]" />
              <span className="text-white font-semibold text-sm">Preferências de Cookies</span>
            </div>
            <button
              onClick={() => onSetShowPreferences(false)}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Fechar preferências"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Categories */}
          <div className="px-5 py-4 space-y-4">
            {/* Essential — always on */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-800">Essenciais</p>
                <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                  Necessários para o funcionamento do site. Sempre ativos.
                </p>
              </div>
              <div className="flex-shrink-0 w-10 h-5 bg-[#2D4A3E] rounded-full flex items-center justify-end px-0.5">
                <div className="w-4 h-4 rounded-full bg-white shadow" />
              </div>
            </div>

            <div className="h-px bg-neutral-100" />

            {categories.map((cat) => (
              <div key={cat.key} className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{cat.label}</p>
                  <p className="text-xs text-neutral-500 mt-0.5 leading-relaxed">{cat.description}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={localConsent[cat.key]}
                  aria-label={`Ativar cookies ${cat.label}`}
                  onClick={() => toggleCategory(cat.key)}
                  className={`flex-shrink-0 w-10 h-5 rounded-full flex items-center px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]/40 focus:ring-offset-1 ${
                    localConsent[cat.key]
                      ? 'bg-[#2D4A3E] justify-end'
                      : 'bg-neutral-200 justify-start'
                  }`}
                >
                  <div className="w-4 h-4 rounded-full bg-white shadow transition-transform" />
                </button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="px-5 pb-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onSavePreferences(localConsent)}
              className="w-full py-2.5 bg-[#2D4A3E] text-white text-sm font-semibold rounded-xl hover:bg-[#1e3329] transition-colors"
            >
              Salvar preferências
            </button>
            <button
              type="button"
              onClick={onAcceptAll}
              className="w-full py-2.5 border border-[#2D4A3E] text-[#2D4A3E] text-sm font-semibold rounded-xl hover:bg-[#2D4A3E]/5 transition-colors"
            >
              Aceitar tudo
            </button>
          </div>

          <p className="text-[10px] text-neutral-400 text-center pb-3 px-4">
            Conforme LGPD (Lei 13.709/2018) e Res. CFP 09/2024.{' '}
            <a href="/privacidade" className="underline hover:text-[#2D4A3E]">
              Política de Privacidade
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Simple banner
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      className="fixed inset-x-0 bottom-0 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm z-[9999] p-4 sm:p-0"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden">
        {/* Top accent */}
        <div className="h-[3px] w-full bg-gradient-to-r from-[#2D4A3E] via-[#C9A96E] to-[#2D4A3E]" />

        <div className="px-5 py-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0">
              <EssenciaLogo size={36} color="#2D4A3E" />
            </div>
            <div>
              <p id="cookie-banner-title" className="text-sm font-bold text-neutral-900">
                Sua privacidade importa
              </p>
              <p id="cookie-banner-desc" className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                Como plataforma de saúde mental, tratamos seus dados com cuidado extra — conforme a{' '}
                <a
                  href="/privacidade"
                  className="font-medium underline underline-offset-2 text-[#2D4A3E] hover:opacity-70 transition-opacity"
                >
                  LGPD
                </a>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onAcceptAll}
              className="w-full py-2.5 bg-[#2D4A3E] text-white text-sm font-semibold rounded-xl hover:bg-[#1e3329] transition-colors active:scale-[0.99]"
            >
              Aceitar tudo
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onOpenPreferences}
                className="flex-1 py-2 border border-neutral-200 text-neutral-600 text-sm font-medium rounded-xl hover:bg-neutral-50 transition-colors"
              >
                Personalizar
              </button>
              <button
                type="button"
                onClick={onRejectAll}
                className="flex-1 py-2 border border-neutral-200 text-neutral-600 text-sm font-medium rounded-xl hover:bg-neutral-50 transition-colors"
              >
                Rejeitar
              </button>
            </div>
          </div>
        </div>

        <p className="text-[10px] text-neutral-400 text-center pb-3 px-4">
          <a href="/privacidade" className="underline hover:text-[#2D4A3E]">
            Política de Privacidade
          </a>
          {' · '}
          <a href="/termos" className="underline hover:text-[#2D4A3E]">
            Termos de Uso
          </a>
        </p>
      </div>
    </div>
  );
}

export default CookieBanner;
